import React, { useEffect, useState } from "react";
import AceEditor from "react-ace";
import "ace-builds/src-noconflict/mode-json5";
import "ace-builds/src-noconflict/theme-github";
import "ace-builds/src-noconflict/snippets/json5";
import "ace-builds/src-noconflict/ext-language_tools";
import { ReactComponent as InfoIcon } from "assets/bruial/infoIcon.svg";
import type {
  IAddBurialPlots,
  IBurialAdd,
  IBurialPlots,
} from "service/interface/burial";
import { cls } from "shared/lib/util/classnames";

interface Props {
  plotJson: IBurialPlots["BurialPlotTypes"][0]["BurialPlotTypeDetails"][0]["data"];
  setPlotJson: React.Dispatch<
    React.SetStateAction<
      IBurialPlots["BurialPlotTypes"][0]["BurialPlotTypeDetails"][0]["data"]
    >
  >;
  data: IBurialAdd;
  isVisible: boolean;
  selectIdx: string;
}

export function BurialPlotDetailTable({
  plotJson,
  setPlotJson,
  data,
  isVisible,
  selectIdx,
}: Props) {
  const [editValue, setEditValue] = useState<string>("");

  useEffect(() => {
    if (isVisible && selectIdx) {
      setEditValue(`[
    ${data.BurialPlots[
      Number(selectIdx)
    ].BurialPlotTypes[0].BurialPlotTypeDetails[0].data.map(
      (detailData, idx) =>
        `${idx !== 0 ? `\n\t` : ""}{
          "name": "${detailData.name}",
          "info": "${detailData.info.replaceAll("\n", "\\n")}",
          "floorTitle": "${detailData.floorTitle}",
          "floorName": ${JSON.stringify(detailData.floorName)},
          "goodFloors": ${JSON.stringify(detailData.goodFloors)},
          "floor": [
            ${detailData.floor.map(
              (floorData, idx) =>
                `${idx !== 0 ? `\n\t\t\t` : ""}{
                "name": "${floorData.name}",
                "prices": ${JSON.stringify(floorData.prices)},
                "unit": "${floorData.unit}"
              }`,
            )}
          ]
        }`,
    )}
      ]`);
    } else if (isVisible && !selectIdx) {
      setEditValue(`[
    {
      "name": "지하 (15년 사용)",
      "info": "가격 포함 내역 : 사용료, 초기 5년분 관리비\\n추가비용 : 관리비 5년 단위\\n선납 : 개인단 25만원/부부단 50만원 (5년), 6년차부터 납부",
      "floorTitle": "단",
      "floorName": ["9", "8", "7", "6", "5", "4", "3", "2", "1"],
      "goodFloors": ["6", "5", "4"],
      "floor": [
          {
              "name": "개인단",
              "prices": ["65", "75", "85", "95", "105", "95", "85", "75", "65"],
              "unit": "원"
          },
          {
              "name" : "부부단",
              "prices" : ["65", "75", "85", "95", "105", "95", "85", "75", "65"],
              "unit": "원"
          }
      ]
    }
]`);
    }
  }, [isVisible]);

  useEffect(() => {
    if (editValue) {
      try {
        const editValueToJson = JSON.parse(editValue);
        setPlotJson(
          editValueToJson.map((json: any, idx: number) => ({
            ...json,
            floorCount: json.floorName?.length ? json.floorName.length : 0,
          })),
        );
      } catch {}
    }
  }, [editValue]);

  useEffect(() => {
    if (plotJson?.length !== 0) {
      const tableLength = plotJson?.length;

      if (tableLength) {
        [...Array(tableLength)].forEach((_, idx) => {
          const tableEle = document.getElementById(`textarea${idx}`);
          if (tableEle) {
            const scrollHeight = tableEle.scrollHeight;
            tableEle.style.height = scrollHeight + "px";
          }
        });
      }
    }
  }, [plotJson]);

  const handleEditor = (value: string) => {
    setEditValue(value);
  };

  return (
    <div className="flex w-full gap-[26px]">
      <div className="min-h-[100px] w-[50%]">
        <div className="flex w-full flex-col gap-[36px]">
          {plotJson?.length !== 0 &&
            plotJson?.map((data, tableIdx) => (
              <div key={tableIdx}>
                <div className="mb-[11px] w-full border-[1px] border-gray-3 bg-white">
                  <div className="text-display-7 text-gray-9 flex h-[70px] w-full items-center justify-center">
                    {data?.name}
                  </div>
                  <div className="flex h-[55px] w-full">
                    <div className="text-display-7 flex w-full items-center justify-center bg-[#f8f8f8]">
                      {data?.floorTitle}
                    </div>
                    {data?.floor?.map((floor, floorIdx) => (
                      <div
                        key={floorIdx}
                        className="text-display-7 flex w-full items-center justify-center bg-[#f8f8f8]"
                      >
                        {floor?.name}
                      </div>
                    ))}
                  </div>
                  {[...Array(plotJson[tableIdx]?.floorCount)]?.map(
                    (_, cellIdx) => (
                      <div
                        key={cellIdx}
                        className={cls(
                          cellIdx % 2 === 0 ? "bg-white" : "bg-[#fbfbfb]",
                          "flex h-[55px] w-full",
                        )}
                      >
                        <div className="flex h-full w-full items-center justify-center">
                          <div
                            className={cls(
                              plotJson[tableIdx]?.goodFloors?.includes(
                                String(plotJson[tableIdx]?.floorName[cellIdx]),
                              ) && "!bg-[#9cd696] !text-white",
                              //   BurialPlots[selectOption.ground].name === "봉안"
                              "flex items-center justify-center whitespace-nowrap rounded-[8px] px-[6px] text-[16px] font-semibold text-gray-5",
                              //     : "",
                            )}
                          >
                            {plotJson[tableIdx]?.floorName[cellIdx]}
                          </div>
                        </div>
                        {plotJson[tableIdx]?.floor?.map(
                          (floorData, floorDataIdx) => (
                            <>
                              {floorData?.prices && (
                                <div
                                  key={floorDataIdx}
                                  className="text-display-7 flex h-full w-full items-center justify-center text-gray-5"
                                >
                                  {floorData?.unit?.includes("원")
                                    ? floorData?.prices[
                                        cellIdx
                                      ]?.toLocaleString()
                                    : floorData?.prices[cellIdx]}
                                  {floorData?.unit}
                                </div>
                              )}
                            </>
                          ),
                        )}
                      </div>
                    ),
                  )}
                </div>
                <div className="flex w-full items-start gap-[32px] rounded-[8px] bg-[#f2f4f5] px-[36px] py-[24px]">
                  <div className="mb-[2px] mt-[3px] flex items-center gap-[6px]">
                    <InfoIcon />
                    <p className="m-0 text-[14px] text-[#454545]">안내사항</p>
                  </div>
                  <textarea
                    id={`textarea${tableIdx}`}
                    value={plotJson[tableIdx]?.info}
                    className="m-0 h-[20px] w-[calc(100%-110px)] resize-none border-none bg-[#f2f4f5] p-0 text-[14px] text-[#9f9f9f] outline-none"
                  />
                </div>
              </div>
            ))}
        </div>
      </div>
      <div className="w-[50%]">
        <AceEditor
          mode="json5"
          theme="github"
          value={editValue}
          width="100%"
          height="100%"
          onChange={handleEditor}
          showPrintMargin={true}
          showGutter={true}
          highlightActiveLine={true}
        />
      </div>
    </div>
  );
}
